/**
 * Data Domain Plugin
 *
 * Core plugin that provides data nodes and endpoint types.
 * These are the fundamental building blocks for data flow in the system.
 */

import type { Plugin, EndpointWithContext } from '../Plugin';
import { BaseDependencies } from '@/nodes/Dependencies';
import { DataEndpoint } from './DataEndpoint';
import type { INode } from '@nodes/INode';

// Data nodes
import { arrayNodeDef } from '@data/nodes/ArrayNode/ArrayNode';
import { gridNodeDef } from '@data/nodes/GridNode/GridNode';
import { pianoRollNodeDef } from '@data/nodes/PianoRollNode/PianoRollNode';
import { snakePatternNodeDef } from '@/plugins/generative/SnakePatternNode/SnakePatternNode';
import { rangeNodeDef } from '@data/nodes/RangeNode/RangeNode';
import { flattenNodeDef } from '@data/nodes/FlattenNode/FlattenNode';
import { clockNodeDef } from '@data/nodes/ClockNode/ClockNode';
import { counterNodeDef } from '@data/nodes/CounterNode/CounterNode';
import { logNodeDef } from '@data/nodes/LogNode/LogNode';
import { patternPlayerNodeDef } from '@data/nodes/PatternPlayerNode/PatternPlayerNode';
import { testNodeDef } from '@data/nodes/TestNode/TestNode';
import { dataMapperNodeDef } from '@data/nodes/DataMapperNode/DataMapperNode';
import { functionNodeDef } from '@data/nodes/FunctionNode/FunctionNode';
import { routerNodeDef } from '@data/nodes/RouterNode/RouterNode';
import { switchInNodeDef, switchOutNodeDef } from '@data/nodes/SwitchNode/SwitchNode';
import { arrayShuffleNodeDef } from '@data/nodes/ArrayShuffleNode/ArrayShuffleNode';
import { arrayGatherNodeDef } from '@data/nodes/ArrayGatherNode/ArrayGatherNode';
import { arrayCycleNodeDef } from '@data/nodes/ArrayCycleNode/ArrayCycleNode';
import { arrayMathNodeDef } from '@data/nodes/ArrayMathNode/ArrayMathNode';

const nodes = [
  arrayNodeDef,
  gridNodeDef,
  pianoRollNodeDef,
  rangeNodeDef,
  flattenNodeDef,
  clockNodeDef,
  counterNodeDef,
  logNodeDef,
  patternPlayerNodeDef,
  testNodeDef,
  dataMapperNodeDef,
  functionNodeDef,
  routerNodeDef,
  switchInNodeDef,
  switchOutNodeDef,
  arrayShuffleNodeDef,
  arrayGatherNodeDef,
  arrayCycleNodeDef,
  arrayMathNodeDef,
];

/**
 * Data Domain Plugin
 *
 * Provides core data types and data manipulation nodes.
 * This is a required plugin that should always be registered.
 */
export const dataPlugin: Plugin = {
  id: 'data',
  name: 'Data Domain',

  // Data endpoint types - all use DataEndpoint
  endpointDataTypes: ['number', 'string', 'boolean', 'array', 'any'],
  nodes,
  createNode(type: string, id: string, deps: BaseDependencies): INode {
    const nodeDef = nodes.find(n => n.type === type);
    if (!nodeDef) {
      throw new Error(`Unknown data node type: ${type}`);
    }
    return new nodeDef.ctor(id, { ...deps, type });
  },

  createEndpoint(config: EndpointWithContext) {
    return new DataEndpoint(config, this.store!);
  },

  async registerUI({ registerViz }) {
    const { GridVisualization } = await import('./nodes/GridNode/GridVisualization');
    const { LogVisualization } = await import('./nodes/LogNode/LogVisualization');
    registerViz('Grid', GridVisualization);
    registerViz('Log', LogVisualization);
    const { ExpressionVisualization } = await import('./nodes/FunctionNode/FunctionVisualization');
    registerViz('Function', ExpressionVisualization);
    const { ArrayVisualization } = await import('./nodes/ArrayNode/ArrayVisualization');
    registerViz('Array', ArrayVisualization);
  }
};
